import { QueryClient } from '@tanstack/react-query';

/**
 * Query defaults tuned for operator work rather than for a consumer app.
 *
 * The governing constraint from the brief: "During live games, staleness is
 * dangerous." So caches are short and refetch-on-focus is on — an operator who
 * tabs back to a draft screen must not be looking at a minute-old clock. Views
 * that are genuinely live (draft state, scoring, queue depth) set their own
 * shorter `refetchInterval` and surface a last-fetched timestamp.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        // Flaky connections are assumed. Retry reads, but bound the delay so a
        // failure surfaces rather than hanging behind an exponential backoff.
        retry: 2,
        retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 4_000),
      },
      mutations: {
        // Never silently retry a write. A Tier 2/3 admin action must land exactly
        // once; a retried force-pick or token adjustment is its own incident.
        retry: false,
      },
    },
  });
}
